import { useEffect, useState } from "react";
import { Input, Label } from "abs-ui-components";
import { MDBRow, MDBCol } from "mdbreact";
import { Tooltip } from "@mui/material";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import "./ShipParticulars.scss";

const ShipParticulars = ({ vesselMstId, monitoringData }) => {
	const {
		control,
		formState: { errors },
	} = useFormContext();
	const [b1ShipDetails, setB1ShipDetails] = useState(null);
	const [loading, setLoading] = useState(true);
	const [fetchError, setFetchError] = useState(null);
	const [hasSaved, setHasSaved] = useState(false);

	useEffect(() => {
		if (monitoringData) {
			setB1ShipDetails(monitoringData);
		}
		setLoading(false);
	}, [monitoringData]);

	const efficiencyLookup = {
		1: "Not Applicable",
		2: "EEDI",
		3: "EIV",
		4: "EEXI",
		5: "Unknown",
	};
	const getEfficiencyData = (mstId) => {
		const match = b1ShipDetails?.VesselTechnicalEfficiencyDtls?.find((d) => d.TechnicalEfficiencyMstId === mstId);
		return {
			type: efficiencyLookup[mstId] ?? "",
			value: match?.TechnicalEfficiencyText ?? 0,
		};
	};

	if (loading) return <div>Loading ship particulars...</div>;
	if (fetchError) return <div>Error: {fetchError}</div>;
	if (!b1ShipDetails) return <div>No ship data available. Please ensure a valid vessel ID is provided.</div>;

	const tooltipTitle = (
		<span>
			Value auto-filled. To update, please modify the details in{" "}
			<a href={`/my-fleet/enroll-vessel/${vesselMstId}`} style={{ color: "blue", textDecoration: "underline" }}>
				MyFleet
			</a>
		</span>
	);

	const renderInputWithTooltip = (field, id, value, error) => (
		<>
			<Tooltip title={tooltipTitle} placement="top" arrow>
				<span>
					<Input id={id} value={value} className="form-input" disabled />
				</span>
			</Tooltip>
			<div className="error-msg-b1ship">{error || <>&nbsp;</>}</div>
		</>
	);

	return (
		<div className="form-section mt-5">
			<MDBRow className="align-items-center mb-2">
				<MDBCol md="4">
					<Label name="Name of the ship" bold />
					<Controller
						name="shipName"
						control={control}
						defaultValue={b1ShipDetails?.shipName ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "name_of_the_ship", field.value, errors?.shipName?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="IMO number" bold />
					<Controller
						name="imoNumber"
						control={control}
						defaultValue={b1ShipDetails?.imoNumber ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "vessel_imo_number", field.value, errors?.imoNumber?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Company" bold />
					<Controller
						name="company"
						control={control}
						defaultValue={b1ShipDetails?.company}
						render={({ field }) => renderInputWithTooltip(field, "ship_owner_name", field.value, errors?.shipOwnerName?.message)}
					/>
				</MDBCol>
			</MDBRow>

			<MDBRow className="align-items-center mb-2">
				<MDBCol md="4">
					<Label name="Flag" bold />
					<Controller
						name="flag"
						control={control}
						defaultValue={b1ShipDetails?.flag ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "flag", field.value, errors?.flag?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Year of delivery" bold />
					<Controller
						name="yearOfDelivery"
						control={control}
						defaultValue={b1ShipDetails?.yearOfDelivery ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "year_of_delivery", field.value, errors?.yearOfDelivery?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Ship type" bold />
					<Controller
						name="shipType"
						control={control}
						defaultValue={b1ShipDetails?.shipType ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "type_of_ship", field.value, errors?.shipType?.message)}
					/>
				</MDBCol>
			</MDBRow>

			<MDBRow className="align-items-center mb-2">
				<MDBCol md="4">
					<Label name="Gross Tonnage" bold />
					<Controller
						name="grossTonnage"
						control={control}
						defaultValue={b1ShipDetails?.grossTonnage ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "gross_tonnage", field.value, errors?.grossTonnage?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Net Tonnage (NT)" bold />
					<Controller
						name="netTonnage"
						control={control}
						defaultValue={b1ShipDetails?.netTonnage ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "net_tonnage", field.value, errors?.netTonnage?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Deadweight tonnage (DWT)" bold />
					<Controller
						name="deadweight"
						control={control}
						defaultValue={b1ShipDetails?.deadweight ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "dead_weight", field.value, errors?.deadweight?.message)}
					/>
				</MDBCol>
			</MDBRow>

			<MDBRow className="align-items-center mb-2">
				<MDBCol md="4">
					<Label name="Attained EEDI (if applicable)" bold />
					<Controller
						name="attainedEEDI"
						control={control}
						defaultValue={b1ShipDetails?.technicalEfficiencies?.find((te) => te.key === "2")?.value ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "attained_eedi", field.value, errors?.attainedEEDI?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Attained EEXI (if applicable)" bold />
					<Controller
						name="attainedEEXI"
						control={control}
						defaultValue={b1ShipDetails?.technicalEfficiencies?.find((te) => te.key === "4")?.value ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "attained_eexi", field.value, errors?.attainedEEXI?.message)}
					/>
				</MDBCol>

				<MDBCol md="4">
					<Label name="Ice Class" bold />
					<Controller
						name="iceClass"
						control={control}
						defaultValue={b1ShipDetails?.iceClass ?? ""}
						render={({ field }) => renderInputWithTooltip(field, "ice_class", field.value, errors?.iceClass?.message)}
					/>
				</MDBCol>
			</MDBRow>
		</div>
	);
};

export default ShipParticulars;
